import { t, Trans } from '@lingui/macro'
import { pdf } from '@react-pdf/renderer'
import { useQueryClient } from '@tanstack/react-query'
import { dateFormat } from 'app/appSettings'
import { queryCreateObject } from 'app/backend/Common'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import { setUserData } from 'app/redux/actions/UserActions'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import SFAuthService from 'app/services/sfAuth/SFAuthService'
import { getAccountsMap } from 'app/services/sfAuth/sfData/sfAccount'
import { enforceQuerySuccess } from 'app/services/sfAuth/sfData/sfCommon'
import { getContactsMapByFlow } from 'app/services/sfAuth/sfData/sfContact'
import { getDocumentsByEntity, uploadFile } from 'app/services/sfAuth/sfData/sfFiles'
import { submitOpportunity } from 'app/services/sfAuth/sfData/sfOpportunity'
import { saveReviewAssignementParsedByFlow } from 'app/services/sfAuth/sfData/sfReviews'
import { useOrganizationValidityBase } from 'app/services/sfAuth/sfHooks/useOrganizationValidityBase'
import { generateUploadFileName } from 'app/utils/generateUploadFileName'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useHistory } from 'react-router-dom'
import { myI18n } from 'translation/I18nConnectedProvider'
import ProgressSnackbar from '../common-components/CustomSnackbars'
import RedirectWarning from '../common-components/RedirectWarning'
import SaveWillOverrideWarningDialog from '../common/SaveWillOverrideWarningDialog'
import SavingFailedWarningDialog from '../common/SavingFailedWarningDialog'
import { FUNDING_PATHS } from '../funding/FundingConstants'
import { GRANTS_PATH } from '../grants/GrantsConstants'
import { RESOURCE_INVENTORY_PATHS } from '../resources/ResourcesConstants'
import './common/FormTypography.scss'
import { formComponentTypes } from './components/formComponentTypes'
import {
  DEFAULT_FORM_SAVE_REJECT,
  DOCUMENT_TYPE_GRANTEE_REPORT,
  extractFormFieldDetails,
  getMainConnected,
  handleFormSave,
  mapFormElements
} from './Form'
import './Form.scss'
import { getDisabledIds } from './formHelpersConditions'
import { getInitialValues } from './formHelpersFormik'
import { isTrueDirty } from './formHelpersValidation'
import { connectedObjectQuery } from './formsHelpersQueries'
import { shouldBlockAutoSaveForUser } from './formUtils'

const FormSavingHandler = ({
  children,
  fetchData,
  loading,
  autosave,
  readOnly,
  displayUnsavedWarning,
  displaySaveFailedDialog,
  formikRef,
  isPreview,
  disabled,
  useMultiuser,
  formId,
  sections,
  elementsMap,
  describeMap,
  connectedMap,
  stage,
  id,
  mainObject,
  generatePDF,
  handleObjectMissing,
  saveCallback,
  submitCallback,
  saveErrorCallback,
  submitErrorCallback,
  configuration,
  data,
  noSaveButton,
  saveDisabled,
  currentStep,
  formTitlePdf,
  ...props
}) => {
  const [saveFailedData, setSaveFailedData] = useState()
  const [overrideWarningData, setOverrideWarningData] = useState()
  const [saving, setSaving] = useState(false)
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)

  const user = useSelector((state) => state.user)
  const blockAutoSave = shouldBlockAutoSaveForUser(user)

  const appConfigurations = useSelector((state) => state.configuration || {})
  const organization = useSelector((state) => state.organization || {})
  const { language } = user
  const langVersion = language?.split('_')[0] || 'en'

  const { values, initialValues, setFieldValue } = useFormikContext()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const history = useHistory()

  const location = useLocation()
  const pathname = location?.pathname

  const { network, fundingStreams, budgetCategories } = useGlobalQueries()
  const queryClient = useQueryClient()

  const { isValid, isLoadedAndInvalid } = useOrganizationValidityBase({
    accountId: organization.id,
    userId: user.userId,
    formId: appConfigurations.FORM_ORGANIZATION_DETAILS
  })

  useEffect(() => {
    // Handle autosave
    const minutes = Number(autosave)

    if (loading || !minutes || isPreview || disabled || readOnly || blockAutoSave || saveDisabled) {
      return
    }

    if (minutes !== 0) {
      const handle = setInterval(() => {
        console.log('try autosave')
        if (formikRef && isTrueDirty(formikRef.current)) {
          const { values } = formikRef.current
          trySaving({ values })
          //   if (useMultiuser) {
          //     // only first user should commence autosave
          //     muFetchAllUsersInfo({
          //       realmId: id,
          //       userId: user.userId
          //     }).then(({ users }) => {
          //       let myUserInfo
          //       Object.values(users).some((obj) => {
          //         if (obj.id === user.userId) {
          //           myUserInfo = obj
          //           return true
          //         }
          //         return false
          //       })
          //       if (myUserInfo && myUserInfo.logOrder === 0) {
          //         trySaving({ values, type: 'Autosave' })
          //       }
          //     })
          //   } else {
          //     trySaving({ values })
          //   }
        }
      }, 60000 * minutes)
      return () => {
        clearInterval(handle)
      }
    }
  }, [currentStep, loading, saving, initialValues, disabled, readOnly])

  const trySaving = useCallback(
    ({ values, type = 'Save' }) => {
      console.log('trySaving', { values, type, data })
      console.log(overrideWarningData, !SFAuthService.user, readOnly, saving, saveFailedData)

      if (overrideWarningData || !SFAuthService.user || readOnly || saving || saveFailedData) {
        return
      }
      setSaving(true)
      //   if (useMultiuser) {
      //     return handleMultiuserSaveRequest({
      //       token: multiuserSessionToken,
      //       saveType: type,
      //       handleBlockSave: () => {
      //         enqueueSnackbar(
      //           <Trans>
      //             You cannot save the form, some fields are currently edited by
      //             other users!
      //           </Trans>,
      //           {
      //             variant: 'error'
      //           }
      //         )
      //         setSaving(false)
      //       }
      //     })
      //   }

      const savingSnackbar = enqueueSnackbar('', {
        persist: true,
        content: (key) => ProgressSnackbar(<Trans>Saving</Trans>)
      })

      const handleError = (err) => {
        console.error('error saving form', err)
        closeSnackbar(savingSnackbar)
        setSaving(false)
        enqueueSnackbar(<Trans>Error ocurred while saving! Some fields were not saved!</Trans>, {
          variant: 'error'
        })
      }

      if (!data?.displayesSavedInMeantimeWarning || useMultiuser) {
        return handleSave({ values, snackbar: savingSnackbar }).catch((err) => {
          // handleError(err)
        })
      }

      return connectedObjectQuery(data, {
        id,
        langVersion,
        enqueueSnackbar,
        handleObjectMissing
      })
        .then((result) => {
          const currentConnectedMap = result.connectedMap
          if (Object.keys(currentConnectedMap).length === 0) {
            enqueueSnackbar(<Trans>You lost connection!</Trans>, {
              variant: 'error'
            })
            return Promise.reject(new Error())
          }
          const wasSavedInMeantime = Object.keys(currentConnectedMap).some((key) => {
            const objectSaved = currentConnectedMap[key].sfObject
            const objectNow = connectedMap[key].sfObject
            const savedDate = moment.utc(objectSaved.LastModifiedDate)
            const currentDate = moment.utc(objectNow.LastModifiedDate)
            return savedDate.isAfter(currentDate)
          })
          if (wasSavedInMeantime) {
            const dataToPass = {
              type: 'form'
            }
            const savedValues = getInitialValues({
              data,
              enqueueSnackbar,
              connectedMap: currentConnectedMap
            })

            const current = {}
            const saved = {}
            const map = mapFormElements(data, langVersion)
            const contacts = []
            const accounts = []
            Object.keys(map).forEach((key) => {
              const question = map[key]
              if (question.elementType === 'connectContact') {
                if (savedValues[key] && savedValues[key] !== values[key]?.id) {
                  contacts.push(savedValues[key])
                }
              } else if (question.elementType === 'connectAccount') {
                if (savedValues[key] && savedValues[key] !== values[key]?.id) {
                  accounts.push(savedValues[key])
                }
              }
            })
            const mapPromise =
              contacts.length > 0 || accounts.length > 0
                ? Promise.all([getContactsMapByFlow(contacts), getAccountsMap(accounts)])
                : Promise.resolve().then((r) => [{}, {}])
            return mapPromise.then(([contactsMap, accountsMap]) => {
              closeSnackbar(savingSnackbar)
              Object.keys(values).forEach((key) => {
                const question = map[key]
                if (question) {
                  dataToPass[key] = question
                  const toText = formComponentTypes[question.elementType].valueToText
                  const parseValue = formComponentTypes[question.elementType].parseValueToCompare
                  const { isConnected } = question.typeProps
                  let connectedFieldDetails
                  const { connectedField, connectedObject } = getMainConnected(question)
                  if (isConnected && connectedField) {
                    connectedFieldDetails = extractFormFieldDetails({
                      connectedField,
                      connectedMap,
                      describeMap,
                      connectedObject
                    })
                  }
                  if (toText) {
                    current[key] = {
                      value: parseValue ? parseValue(values[key]) : values[key],
                      ...toText(values[key], question, {
                        contactsMap,
                        accountsMap,
                        connectedFieldDetails
                      })
                    }
                    saved[key] = {
                      value: parseValue ? parseValue(savedValues[key]) : savedValues[key],
                      ...toText(savedValues[key], question, {
                        contactsMap,
                        accountsMap,
                        connectedFieldDetails
                      })
                    }
                  } else {
                    console.warn('No value to text function configured for: ', question.elementType)
                  }
                }
              })
              setOverrideWarningData({
                current,
                saved,
                formData: dataToPass
              })
              return Promise.resolve()
            })
          } else {
            handleSave({ values, snackbar: savingSnackbar })
          }
        })
        .catch((err) => {
          handleError(err)
        })
    },
    [data, connectedMap, saving, saveFailedData, overrideWarningData]
  )

  const handleSave = ({ values, snackbar }) => {
    const savingSnackbar =
      snackbar ||
      enqueueSnackbar('', {
        persist: true,
        content: (key) => ProgressSnackbar(<Trans>Saving</Trans>)
      })
    const _connectedMap = props.saveConnectedMap || connectedMap
    const disabledIds = getDisabledIds({
      sections,
      elementsMap,
      values,
      langVersion,
      connectedMap: _connectedMap,
      describeMap,
      errors: formikRef.current.errors,
      pathname
    })
    const isValid =
      Object.keys(formikRef.current.errors).filter((key) => !disabledIds.includes(key)).length === 0
    return handleFormSave({
      values,
      elementsMap,
      connectedMap: _connectedMap,
      appConfigurations: fundingStreams,
      disabledIds,
      reduxBag: {
        dispatch,
        user,
        organization,
        appConfigurations: fundingStreams
      },
      mainObject,
      utilityBag: {
        history,
        pathname,
        closeSnackbar,
        enqueueSnackbar,
        formId,
        stage
      },
      network,
      budgetCategories
    })
      .then(
        (result) => {
          closeSnackbar(savingSnackbar)
          setSaving(false)

          console.log('form saved', result)

          enqueueSnackbar(<Trans>Successfully saved!</Trans>, {
            variant: 'success'
          })
          if (formId === appConfigurations.FORM_USER_PROFILE) {
            dispatch(setUserData({ isUserProfileValid: Boolean(isValid) }))
          }

          if (saveCallback) {
            const savedMap = {}
            Object.entries(elementsMap).forEach(([key, element]) => {
              const fieldValue = values[key]
              const { connectedField, connectedObject } = getMainConnected(element)
              if (connectedField && connectedObject) {
                const objectType =
                  _connectedMap[connectedObject]?.objectType ||
                  _connectedMap[connectedObject]?.sfObject?.attributes?.type
                if (!savedMap[objectType]) {
                  savedMap[objectType] = {}
                }
                savedMap[objectType][connectedField.name] = fieldValue
              }
            })

            if (savedMap.Review_Assignement__c) {
              savedMap.Review_Assignement__c.Last_modified_through_portal_by__c = user.userId
              savedMap.Review_Assignement__c.Last_modified_by_portal_user__c = moment.utc()
              savedMap.Review_Assignement__c.Form__c = formId
            }

            saveCallback({
              isValid,
              savedMap,
              result,
              setFieldValue
            })
          }

          fetchData({}).then((r) => {
            console.log('data refetched', r)
            queryClient.invalidateQueries({
              queryKey: ['availableAccounts', user.userId]
            })

            closeSnackbar(savingSnackbar)
            setSaving(false)
            // enqueueSnackbar(<Trans>Successfully saved!</Trans>, {
            //   variant: 'success'
            // })
            if (formId === appConfigurations.FORM_USER_PROFILE) {
              dispatch(setUserData({ isUserProfileValid: Boolean(isValid) }))
            }
            if (saveCallback) {
              const savedMap = {}
              Object.entries(elementsMap).forEach(([key, element]) => {
                const fieldValue = values[key]
                const { connectedField, connectedObject } = getMainConnected(element)
                if (connectedField && connectedObject) {
                  const objectType =
                    _connectedMap[connectedObject]?.objectType ||
                    _connectedMap[connectedObject]?.sfObject?.attributes?.type
                  if (!savedMap[objectType]) {
                    savedMap[objectType] = {}
                  }
                  savedMap[objectType][connectedField.name] = fieldValue
                }
              })
              if (savedMap.Review_Assignement__c) {
                savedMap.Review_Assignement__c.Last_modified_through_portal_by__c = user.userId
                savedMap.Review_Assignement__c.Last_modified_by_portal_user__c = moment.utc()
                savedMap.Review_Assignement__c.Form__c = formId
              }
              saveCallback({
                isValid,
                savedMap,
                result,
                setFieldValue
              })
            }
          })
        },
        (rejectObj) => {
          const { reject, extractedError, showSnackbar, savingResult } = rejectObj
          closeSnackbar(savingSnackbar)
          setSaving(false)
          const current = {}
          const map = mapFormElements(data, langVersion)
          const dataToPass = {
            type: 'form'
          }
          Object.keys(values).forEach((key) => {
            const question = map[key]
            if (question) {
              dataToPass[key] = question
              const toText = formComponentTypes[question.elementType].valueToText
              const parseValue = formComponentTypes[question.elementType].parseValueToCompare
              const { isConnected } = question.typeProps
              let connectedFieldDetails
              const { connectedField, connectedObject } = getMainConnected(question)
              if (isConnected && connectedField) {
                connectedFieldDetails = extractFormFieldDetails({
                  connectedField,
                  connectedMap: _connectedMap,
                  describeMap,
                  connectedObject
                })
              }
              if (toText) {
                current[key] = {
                  title: question.title,
                  value: parseValue ? parseValue(values[key], { saveFailed: true }) : values[key],
                  ...toText(values[key], question, {
                    connectedFieldDetails,
                    saveFailed: true
                  })
                }
              } else {
                console.warn('No value to text function configured for: ', question.elementType)
              }
            }
          })

          if (showSnackbar) {
            enqueueSnackbar(
              <Trans>Error ocurred while saving! Some fields were not saved!</Trans>,
              {
                variant: 'error'
              }
            )
          }

          if (data?.displaySaveFailedDialog && reject === DEFAULT_FORM_SAVE_REJECT) {
            setSaveFailedData({
              current,
              savingResult,
              connectedMap: props.saveConnectedMap || connectedMap,
              error: extractedError,
              formData: dataToPass
            })
            // Non-general saving error can result in partial data being created, so we need to look if something was actually created and update the form values with IDs
            // TODO Move into better saving, using extranal IDs & upsert
            enforceQuerySuccess(() => fetchData({ preserveValues: true }), 3)
            return rejectObj
          }

          const stringifiedLog = JSON.stringify({
            reject,
            savingResult,
            extractedError
          })
          queryCreateObject({
            type: 'Error Log',
            createData: {
              severity: 'error',
              description: sfOauthConfig.oauth2.redirectUri + history.location.pathname,
              longDescription:
                stringifiedLog.length < 131072 ? stringifiedLog : 'Error log was too long'
            }
          })
          // Non-general saving error can result in partial data being created, so we need to look if something was actually created and update the form values with IDs
          // TODO Move into better saving, using extranal IDs & upsert
          enforceQuerySuccess(fetchData({ preserveValues: true }), 3)
          return rejectObj
        }
      )
      .catch((err) => {
        setSaving(false)
        closeSnackbar(snackbar)
        if (saveErrorCallback) {
          saveErrorCallback(err)
        }
      })
  }

  const _handleSubmit = ({ values, pdfDocument }) => {
    setSaving(true)
    const submitText = <Trans>Submitting</Trans>

    const snackKey = enqueueSnackbar('', {
      variant: 'info',
      persist: true,
      content: (key) => ProgressSnackbar(submitText)
    })

    const baseHandleError = (error, snackbarText) => {
      console.log('error submitting', error)
      closeSnackbar(snackKey)
      enqueueSnackbar(snackbarText || <Trans>Error Submitting</Trans>, {
        variant: 'error'
      })
    }

    const baseHandleSuccess = ({ result, successText }) => {
      console.log('submitted and got ', result)
      return fetchData({}).then(() => {
        enqueueSnackbar(successText, {
          variant: 'success'
        })
      })
    }
    const disabledIds = getDisabledIds({
      sections,
      elementsMap,
      values,
      langVersion,
      connectedMap,
      describeMap,
      errors: formikRef.current.errors,
      pathname
    })
    const submitConnectedMap = props.saveConnectedMap || connectedMap
    const baseToSave = structuredClone(props.baseToSave || {})

    Object.entries(submitConnectedMap).forEach(([key, obj]) => {
      const { sfObject } = obj
      const type = sfObject.attributes.type
      const existingValues = baseToSave[key] || {}
      if (type === mainObject) {
        if (type === 'Resource__c') {
          baseToSave[key] = {
            ...existingValues,
            Id: sfObject.Id,
            Stage__c: 'Submitted',
            Organization__c: organization?.id,
            Last_Modified_By_User__c: user.userId,
            Last_Modified_By_User_Date__c: moment.utc()
          }
        } else if (type === 'Pre_Qualification__c') {
          baseToSave[key] = {
            ...existingValues,
            Id: sfObject.Id,
            Stage__c: 'Submitted'
          }
        } else if (type === 'FGM_Base__Grantee_Report__c') {
          baseToSave[key] = {
            ...existingValues,
            Id: sfObject.Id,
            FGM_Base__Status__c: 'Submitted'
          }
        } else if (type === 'Review_Assignement__c') {
          baseToSave[key] = {
            ...existingValues,
            Id: sfObject.Id,
            Stage__c: 'Submitted',
            Last_modified_through_portal_by__c: user.userId,
            Last_modified_by_portal_user__c: moment.utc(),
            Form__c: formId
          }
        }
      }
    })

    return handleFormSave({
      values,
      elementsMap,
      connectedMap: submitConnectedMap,
      baseToSave,
      appConfigurations: fundingStreams,
      disabledIds,
      reduxBag: {
        dispatch,
        user,
        organization
      },
      isSubmitting: true,
      mainObject,
      budgetCategories,
      utilityBag: {
        history,
        pathname,
        closeSnackbar,
        enqueueSnackbar,
        formId,
        stage
      },
      network
    })
      .then((result) => {
        if (submitConnectedMap && Object.keys(submitConnectedMap).length === 0) {
          enqueueSnackbar(<Trans>There is no object to submit!</Trans>, {
            variant: 'error'
          })
        } else {
          const promises = []
          Object.values(submitConnectedMap).forEach(async (data) => {
            const connectedObject = data?.sfObject
            const type = connectedObject.attributes.type

            if (mainObject === type) {
              // TODO Move promises from here if possible to handling before
              if (type === 'Opportunity') {
                if (!isValid) {
                  baseHandleError(
                    'organization details form is not valid',
                    <Trans>
                      You cannot submit the application until you fill out all field marked as
                      required in the Organisation Details
                    </Trans>
                  )
                  promises.push(Promise.reject(new Error('organization details form is not valid')))
                } else {
                  formikRef.current.setFieldValue('kickedOutOfForm', true, false)
                  promises.push(
                    Promise.all([
                      submitOpportunity(connectedObject.Id),
                      pdfDocument
                        ? pdf(pdfDocument)
                            .toBlob()
                            .then(async (blob) => {
                              const uploadedFiles = await getDocumentsByEntity(connectedObject.Id)
                              const filteredFiles = uploadedFiles.filter(
                                (doc) => doc.ContentVersion.Type__c === 'Application'
                              )
                              const name = generateUploadFileName({
                                opportunity: connectedObject,
                                type: 'Application',
                                existingDocsCount: filteredFiles.length
                              })
                              return blob.arrayBuffer().then((buffer) => {
                                return uploadFile(
                                  {
                                    name,
                                    tags: 'grant_application',
                                    type: 'Application',
                                    opportunityId: connectedObject.Id
                                  },
                                  buffer,
                                  network.id,
                                  true
                                )
                              })
                            })
                            .catch((pdfError) => {
                              const stringifiedLog = JSON.stringify({
                                type: 'PDF creation fail',
                                reject: String(pdfError),
                                objectType: type,
                                objectId: connectedObject.Id
                              })
                              queryCreateObject({
                                type: 'Error Log',
                                createData: {
                                  severity: 'error',
                                  description: sfOauthConfig.oauth2.redirectUri + pathname,
                                  longDescription:
                                    stringifiedLog.length < 131072
                                      ? stringifiedLog
                                      : 'Error log was too long'
                                }
                              })
                              throw pdfError
                            })
                        : Promise.resolve()
                    ])
                      .then((res) => {
                        enqueueSnackbar(<Trans>Submitted Application</Trans>, {
                          variant: 'success'
                        })
                        closeSnackbar(snackKey)
                        history.push(FUNDING_PATHS.APPLICATIONS_LIST)
                      })
                      .catch((error) => {
                        baseHandleError(error)
                      })
                  )
                }
              } else if (type === 'Pre_Qualification__c') {
                formikRef.current.setFieldValue('kickedOutOfForm', true, false)
                const uploadedFiles = await getDocumentsByEntity(connectedObject.Id)
                const filteredFiles = uploadedFiles.filter(
                  (doc) => doc.ContentVersion.Type__c === 'Pre-qualification'
                )
                const name = generateUploadFileName({
                  type: 'Pre-qualification',
                  name: connectedObject.Name,
                  existingDocsCount: filteredFiles.length
                })
                promises.push(
                  pdf(pdfDocument)
                    .toBlob()
                    .then((blob) => {
                      return blob.arrayBuffer().then((buffer) => {
                        return uploadFile(
                          {
                            name,
                            tags: 'pre_qualification',
                            type: 'Pre-qualification',
                            opportunityId: connectedObject.Id
                          },
                          buffer,
                          network.id,
                          true
                        ).then((result) => {
                          enqueueSnackbar(<Trans>SUBMITTED_PREQUALIFICATION_SNACKBAR</Trans>, {
                            variant: 'success'
                          })
                          closeSnackbar(snackKey)
                          history.push(FUNDING_PATHS.APPLICATIONS_LIST)
                        })
                      })
                    })
                    .then(
                      (result) => {},
                      (reject) => {
                        const stringifiedLog = JSON.stringify({
                          type: 'PDF creation fail',
                          reject: String(reject),
                          objectType: type,
                          objectId: connectedObject.Id,
                          fileName: name
                        })
                        queryCreateObject({
                          type: 'Error Log',
                          createData: {
                            severity: 'error',
                            description: sfOauthConfig.oauth2.redirectUri + pathname,
                            longDescription:
                              stringifiedLog.length < 131072
                                ? stringifiedLog
                                : 'Error log was too long'
                          }
                        })
                        baseHandleError(reject)
                      }
                    )
                )
              } else if (type === 'Resource__c') {
                formikRef.current.setFieldValue('kickedOutOfForm', true, false)
                enqueueSnackbar(<Trans>RESOURCE_SUBMITTED</Trans>, {
                  variant: 'success'
                })
                history.push(RESOURCE_INVENTORY_PATHS.resourceInventoryMyEntries)
              } else if (type === 'Review_Assignement__c') {
                formikRef.current.setFieldValue('kickedOutOfForm', true, false)
                promises.push(
                  saveReviewAssignementParsedByFlow({
                    id: connectedObject.Id,
                    status: 'Submitted'
                  }).then(
                    (result) => {
                      enqueueSnackbar(<Trans>Review submitted</Trans>, {
                        variant: 'success'
                      })
                      closeSnackbar(snackKey)
                    },
                    (reject) => {
                      baseHandleError(reject)
                    }
                  )
                )
              } else if (type === 'FGM_Base__Grantee_Report__c') {
                formikRef.current.setFieldValue('kickedOutOfForm', true, false)
                const opportunity = Object.values(connectedMap).find((item) => {
                  const sfObject = item?.sfObject
                  const type = sfObject?.attributes?.type
                  return type === 'Opportunity'
                })
                const interimReports = (opportunity?.additionalInfo?.reports || []).filter(
                  (report) => report.Type__c === 'Interim'
                )
                const uploadedFiles = await getDocumentsByEntity(connectedObject.Id)
                const filteredFiles = uploadedFiles.filter(
                  (doc) => doc.ContentVersion.Type__c === 'Grantee report'
                )
                const granteeReportTypes =
                  describeMap?.FGM_Base__Grantee_Report__c?.fieldsMap?.Type__c?.picklistValues || []
                const granteeReportType = granteeReportTypes?.find(
                  (type) => type.value === connectedObject?.Type__c
                )
                const progressReportNumber =
                  granteeReportType?.value === 'Interim' && interimReports?.length > 0
                    ? interimReports?.length + 1
                    : null
                const name = generateUploadFileName({
                  type: 'Grantee report',
                  opportunity: opportunity?.sfObject,
                  existingDocsCount: filteredFiles.length,
                  reportType: granteeReportType?.label,
                  progressReportNumber
                })
                promises.push(
                  pdf(pdfDocument)
                    .toBlob()
                    .then((blob) => {
                      return blob.arrayBuffer().then((buffer) => {
                        return uploadFile(
                          {
                            name,
                            tags: 'grantee_report',
                            type: DOCUMENT_TYPE_GRANTEE_REPORT,
                            opportunityId: connectedObject.Id
                          },
                          buffer,
                          network.id,
                          true
                        )
                      })
                    })
                    .then(
                      (result) => {
                        enqueueSnackbar(<Trans>Submitted Report</Trans>, {
                          variant: 'success'
                        })
                        closeSnackbar(snackKey)
                        history.push(GRANTS_PATH.REPORTS)
                      },
                      (reject) => {
                        const stringifiedLog = JSON.stringify({
                          type: 'PDF creation fail',
                          reject: String(reject),
                          objectType: type,
                          objectId: connectedObject.Id,
                          fileName: name
                        })
                        queryCreateObject({
                          type: 'Error Log',
                          createData: {
                            severity: 'error',
                            description: sfOauthConfig.oauth2.redirectUri + pathname,
                            longDescription:
                              stringifiedLog.length < 131072
                                ? stringifiedLog
                                : 'Error log was too long'
                          }
                        })
                        baseHandleError(reject)
                      }
                    )
                )
              }
            }
          })
          return Promise.all(promises)
            .then((promisesResult) => {
              queryClient.invalidateQueries({
                queryKey: ['availableAccounts', user.userId]
              })
              if (!formikRef.current?.values?.kickedOutOfForm) {
                setSaving(false)
                closeSnackbar(snackKey)
              }
              if (submitCallback) {
                submitCallback({ result, promisesResult })
              }
            })
            .catch((error) => {
              console.log('error submitting', error)
              closeSnackbar(snackKey)
              setSaving(false)
              return error
            })
        }
      })
      .catch((err) => {
        console.log({ err })
        closeSnackbar(snackKey)
        setSaving(false)
        if (submitErrorCallback) {
          submitErrorCallback(err)
        }
      })
  }

  const handleSubmit = props.confirmationDialog
    ? () => {
        setOpenConfirmationDialog(true)
      }
    : _handleSubmit

  const confirmationDialog = props.confirmationDialog
    ? React.cloneElement(props.confirmationDialog, {
        open: openConfirmationDialog,
        setOpen: setOpenConfirmationDialog,
        submitCallback: () => {
          const pdfDocument = generatePDF({
            shouldCalculateFixedElementsInHook: false
          })
          _handleSubmit({ values, pdfDocument })
        }
      })
    : null

  const dirty = isTrueDirty(values, initialValues)

  return (
    <>
      {confirmationDialog}
      {displaySaveFailedDialog && (
        <SavingFailedWarningDialog
          open={Boolean(saveFailedData)}
          data={saveFailedData}
          handleClose={() => {
            setSaveFailedData(null)
          }}
          fileName={myI18n._(t`Extracted data`) + ' - ' + moment.utc().format(dateFormat)}
        />
      )}
      {displayUnsavedWarning && !noSaveButton && !saveDisabled && (
        <RedirectWarning
          open={dirty && !values.kickedOutOfForm}
          handleSave={() => {
            handleSave({ values })
          }}
        />
      )}
      <SaveWillOverrideWarningDialog
        handleSave={() => {
          handleSave({ values })
        }}
        handleClose={() => {
          setOverrideWarningData(null)
          setSaving(false)
        }}
        open={Boolean(overrideWarningData)}
        data={overrideWarningData}
      />
      {children({ trySaving, handleSave, handleSubmit, saving, setSaving })}
    </>
  )
}

export default FormSavingHandler
